import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/pfc";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, DollarSign } from "lucide-react";

const LISTING_TYPES = [
  { v: "service", l: "Service" },
  { v: "product", l: "Product" },
  { v: "donation", l: "Donation / Cause" },
];

export default function MyListingsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/listings/mine");
      setItems(data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/listings/mine/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="listings-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">My Services &amp; Products</h2>
          <p className="text-white/60 text-sm mt-1">List up to 5 items that appear on your profile. Buyers checkout via Stripe (or you can link out to your own page).</p>
        </div>
        <button
          onClick={() => setEditing({ title: "", description: "", price: 0, image: "", listing_type: "service", external_url: "", active: true })}
          disabled={items.length >= 5}
          data-testid="add-listing-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold-bright disabled:opacity-50"
        >
          <Plus size={14} /> Add Listing
        </button>
      </div>

      {loading ? (
        <div className="text-white/40 py-12 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-white/40 py-12 text-center border border-dashed border-white/10 mt-6">No listings yet. Add your first service or product.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-testid="listings-grid">
          {items.map((l) => (
            <div key={l.listing_id} className="border border-white/10 bg-ink-800/30 p-4 flex gap-4" data-testid={`listing-${l.listing_id}`}>
              <div className="w-20 h-20 bg-white/5 shrink-0 overflow-hidden">
                {l.image ? <img src={l.image} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-white/30"><Package size={20} /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-sub text-pfc-gold">
                  {l.listing_type}
                  {!l.active && <span className="text-white/40">· hidden</span>}
                </div>
                <div className="font-sub text-white truncate">{l.title}</div>
                <div className="text-pfc-gold font-display text-lg">${Number(l.price).toFixed(2)}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setEditing(l)} className="px-3 py-1 border border-white/15 text-[11px] uppercase tracking-[0.2em] font-sub hover:bg-white/5 inline-flex items-center gap-1" data-testid={`edit-listing-${l.listing_id}`}><Pencil size={11} /> Edit</button>
                  <button onClick={() => del(l.listing_id)} className="px-3 py-1 border border-pfc-red/30 text-pfc-red text-[11px] hover:bg-pfc-red/10 inline-flex items-center gap-1" data-testid={`del-listing-${l.listing_id}`}><Trash2 size={11} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ListingEditor
          listing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ListingEditor({ listing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: listing.title || "",
    description: listing.description || "",
    price: listing.price ?? 0,
    image: listing.image || "",
    listing_type: listing.listing_type || "service",
    external_url: listing.external_url || "",
    active: listing.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (listing.listing_id) {
        await api.put(`/listings/mine/${listing.listing_id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/listings/mine", payload);
        toast.success("Listing created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="listing-editor">
      <div className="bg-ink-900 border border-pfc-gold/40 max-w-md w-full p-7">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-display text-2xl">{listing.listing_id ? "Edit Listing" : "Add Listing"}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm">Close</button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <I label="Title" required value={form.title} onChange={(v) => upd("title", v)} testId="lst-title" />
          <T label="Description" required value={form.description} onChange={(v) => upd("description", v)} testId="lst-desc" />
          <div className="grid grid-cols-2 gap-3">
            <I label="Price (USD, 0 = inquiry)" type="number" step="0.01" min="0" value={form.price} onChange={(v) => upd("price", v)} testId="lst-price" />
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">Type</div>
              <select value={form.listing_type} onChange={(e) => upd("listing_type", e.target.value)} data-testid="lst-type" className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none">
                {LISTING_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </label>
          </div>
          <I label="Image URL" value={form.image} onChange={(v) => upd("image", v)} testId="lst-image" />
          <I label="External Buy URL (optional)" value={form.external_url} onChange={(v) => upd("external_url", v)} placeholder="If set, Buy redirects there" testId="lst-ext" />
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} data-testid="lst-active" /> Show on my profile
          </label>
          <button type="submit" disabled={saving} data-testid="save-listing-btn" className="w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright disabled:opacity-50">
            {saving ? "Saving…" : "Save Listing"}
          </button>
        </form>
      </div>
    </div>
  );
}

function I({ label, value, onChange, required, type = "text", placeholder, step, min, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <input type={type} step={step} min={min} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
    </label>
  );
}
function T({ label, value, onChange, required, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <textarea required={required} value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none resize-y" />
    </label>
  );
}
